import React, { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import type { BaseEntity, EntityHooks, FieldValue, SimpleTableConfig } from "../../types";
import type { EditingState } from "../types";
import { EditableField } from "./EditableField";
import { CardActionMenu } from "./CardActionMenu";
import { CardFieldHelpPopover } from "@/components/shared/FieldHelp/FieldHelpPopover";

interface CardHeaderSectionProps<TData extends BaseEntity> {
  item: TData;
  config: SimpleTableConfig<TData>;
  selectedRows: Set<string>;
  primaryField?: string;
  secondaryField?: string;
  editingCell: EditingState | null;
  editValue: string;
  onSelectRow: (rowId: string) => void;
  onEditChange: (value: string) => void;
  onEdit: (rowId: string, fieldId: string, currentValue: FieldValue) => void;
  onSave: (rowId: string, fieldId: string) => void;
  onCancel: () => void;
  renderValue: (item: TData, fieldId: string) => React.ReactNode;
  hooks?: EntityHooks<TData>;
  inlineEditingDisabled?: boolean;
}

export function CardHeaderSection<TData extends BaseEntity>({
  item,
  config,
  selectedRows,
  primaryField,
  secondaryField,
  editingCell,
  editValue,
  onSelectRow,
  onEditChange,
  onEdit,
  onSave,
  onCancel,
  renderValue,
  hooks,
  inlineEditingDisabled = false,
}: CardHeaderSectionProps<TData>) {
  // Fields shown on this card — drives the single progressive-disclosure "?"
  // that explains the card's fields without one icon per field.
  const cardFieldIds = useMemo(() => {
    const ids = new Set<string>();
    if (primaryField) ids.add(primaryField);
    if (secondaryField) ids.add(secondaryField);
    Object.entries(config.fields).forEach(([id, fc]) => {
      if (fc.showOnCard) ids.add(id);
    });
    return Array.from(ids);
  }, [config.fields, primaryField, secondaryField]);

  return (
    <div className="px-4 pt-3 pb-3">
      {/* Row 1: Title (most important, read first) + action menu */}
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          {primaryField && (
            <div className="text-[0.9375rem] font-semibold leading-snug tracking-tight text-foreground line-clamp-2">
              <EditableField
                item={item}
                fieldId={primaryField}
                editingCell={editingCell}
                editValue={editValue}
                onEditChange={onEditChange}
                onEdit={onEdit}
                onSave={onSave}
                onCancel={onCancel}
                renderValue={renderValue}
                onClick={inlineEditingDisabled ? undefined : (e) => e.stopPropagation()}
                readOnly={inlineEditingDisabled}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 -mt-0.5">
          <span onClick={(e) => e.stopPropagation()}>
            <CardFieldHelpPopover entity={config.entity} fieldIds={cardFieldIds} side="bottom" align="end" />
          </span>
          {hooks && config.detailActions && config.detailActions.length > 0 && (
            <span onClick={(e) => e.stopPropagation()}>
              <CardActionMenu item={item} config={config} hooks={hooks} onClick={(e) => e.stopPropagation()} />
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Stage badge (left) + checkbox (right, hover-reveal) */}
      <div className="flex items-center justify-between gap-2">
        {secondaryField && (
          <div className="text-sm min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
            <EditableField
              item={item}
              fieldId={secondaryField}
              editingCell={editingCell}
              editValue={editValue}
              onEditChange={onEditChange}
              onEdit={onEdit}
              onSave={onSave}
              onCancel={onCancel}
              renderValue={renderValue}
              onClick={inlineEditingDisabled ? undefined : (e) => e.stopPropagation()}
              readOnly={inlineEditingDisabled}
            />
          </div>
        )}
        <Checkbox
          checked={selectedRows.has(item.id)}
          onCheckedChange={() => onSelectRow(item.id)}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 opacity-0 group-hover/entity-card:opacity-100 data-[state=checked]:opacity-100 transition-opacity"
        />
      </div>
    </div>
  );
}
